import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FileSearch, 
  Calendar, 
  User, 
  Clock, 
  Activity,
  Search,
  Filter,
  Download,
  RefreshCw,
  Crown,
  AlertCircle
} from 'lucide-react';
import { useRealtime } from '../../contexts/RealtimeContext';
import { getAdminLogs, getAllAdmins } from '../../firebase/collections';
import { formatTimestamp } from '../../utils/timestampUtils';
import toast from 'react-hot-toast';

const AdminLogsViewer = () => {
  const { currentUser } = useRealtime();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedAdmin, setSelectedAdmin] = useState('');
  const [admins, setAdmins] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadAdmins();
    loadLogs();
  }, [selectedDate, selectedAdmin]);

  const loadAdmins = async () => {
    try {
      const result = await getAllAdmins();
      if (result.success) {
        setAdmins(result.data || []);
      }
    } catch (error) {
      console.error('Error loading admins:', error);
    }
  };

  const loadLogs = async () => {
    try {
      setLoading(true);
      const result = await getAdminLogs(
        selectedAdmin || null,
        selectedDate || null
      );

      if (result.success) {
        setLogs(result.data || []);
      } else {
        toast.error('Failed to load logs: ' + result.error);
      }
    } catch (error) {
      toast.error('Error loading logs: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getAdminEmail = (adminId) => {
    const admin = admins.find(a => a.id === adminId);
    return admin?.email || adminId;
  };

  const getAdminName = (adminId) => {
    const admin = admins.find(a => a.id === adminId);
    return admin?.displayName || admin?.name || admin?.email || 'Unknown Admin';
  };

  const filteredLogs = logs.filter(log => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      log.action?.toLowerCase().includes(searchLower) ||
      log.details?.toString().toLowerCase().includes(searchLower) ||
      getAdminEmail(log.adminId)?.toLowerCase().includes(searchLower)
    );
  });

  const todayLogs = filteredLogs.filter(log => {
    const logDate = log.timestamp instanceof Date 
      ? log.timestamp 
      : new Date(log.timestamp);
    const today = new Date(selectedDate);
    return logDate.toDateString() === today.toDateString();
  });

  const logsByAdmin = filteredLogs.reduce((acc, log) => {
    const email = getAdminEmail(log.adminId);
    if (!acc[email]) {
      acc[email] = [];
    }
    acc[email].push(log);
    return acc;
  }, {});

  if (loading && logs.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Crown className="h-8 w-8 text-yellow-500" />
            Admin Activity Logs
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Monitor daily admin activities and actions
          </p>
        </div>
        <button
          onClick={loadLogs}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Admin
            </label>
            <select
              value={selectedAdmin}
              onChange={(e) => setSelectedAdmin(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="">All Admins</option>
              {admins.map(admin => (
                <option key={admin.id} value={admin.email}>
                  {admin.displayName || admin.name || admin.email}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search actions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Logs Today</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                {todayLogs.length}
              </p>
            </div>
            <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-full">
              <Activity className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Active Admins</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                {Object.keys(logsByAdmin).length}
              </p>
            </div>
            <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-full">
              <User className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Logs</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                {filteredLogs.length}
              </p>
            </div>
            <div className="bg-purple-100 dark:bg-purple-900/30 p-3 rounded-full">
              <FileSearch className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Logs List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Activity Logs - {new Date(selectedDate).toLocaleDateString()}
          </h2>
        </div>
        <div className="overflow-x-auto">
          {filteredLogs.length > 0 ? (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredLogs.map((log) => (
                <motion.div
                  key={log.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-semibold">
                        {getAdminName(log.adminId)[0]}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {getAdminName(log.adminId)}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {getAdminEmail(log.adminId)}
                          </p>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {log.timestamp ? formatTimestamp(log.timestamp) : 'Unknown time'}
                        </div>
                      </div>
                      <div className="mt-2">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {log.action || 'Unknown Action'}
                        </p>
                        {log.details && (
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                            {typeof log.details === 'object' 
                              ? JSON.stringify(log.details, null, 2)
                              : log.details}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">
                No logs found for the selected date and filters.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLogsViewer;

